import { FC, useEffect, useMemo, useState, useRef, memo } from "react"
import { Info, InfoSettings, LongShortContainer, TitlesContainer, UpperInfoStyled } from "./UpperInfo.styled"
import {themeStore, TType, UpperInfoProps, DexStatistics, StringContainer, showNotification, BLUE, WHITE} from "@root/common";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { getCloseSpread, getEnterSpread, getLongShortTitles } from "../utils";

const NOTIFICATIONS_DELAY_MS = 1 * 60 * 1e3

interface IUpperInfo {
    statistics?: DexStatistics;
    type: TType;
    coin: string
    isVisible: boolean;
}

const UpperInfo: FC<UpperInfoProps> = observer((props) => {
  const {statistics, coin, isVisible} = props as IUpperInfo

  const inputRef = useRef(null)
  const outputRef = useRef(null)

  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')

  const lastNotificationSentTime = useRef<number>(0)
  const delayTimeout = useRef<NodeJS.Timeout>()

  const [enterSpread, closeSpread] = useMemo(() => {
    return [getEnterSpread(statistics), getCloseSpread(statistics)]
  }, [statistics])

  useEffect(() => {
  if (input && parseFloat(enterSpread) > parseFloat(input) || output && parseFloat(enterSpread) < parseFloat(output)) {
    if (lastNotificationSentTime.current === 0) {
      return;
    }
    if (Date.now() - lastNotificationSentTime.current < NOTIFICATIONS_DELAY_MS) {
      return;
    }
    showNotification({title: `${coin} | ${statistics?.name}`, body: `${enterSpread}/${closeSpread}% | ${statistics?.longShort === "long" ? statistics?.name : statistics?.chain} - ${ statistics?.longShort === "long" ? statistics?.chain : statistics?.name}`})
    lastNotificationSentTime.current = Date.now()
  }
}, [enterSpread, closeSpread])

  useEffect(() => {
    clearTimeout(delayTimeout.current)
    delayTimeout.current = setTimeout(() => {
      if (!input && !output) {
        lastNotificationSentTime.current = 0
      } else {
        lastNotificationSentTime.current = 1;
      }
    }, 1e3)
  }, [input, output])


  useEffect(() => {
      if (isVisible) {
          const enterSpread = statistics ? getEnterSpread(statistics) : ""
          const closeSpread = statistics ? getCloseSpread(statistics) : ""
          document.title = `${enterSpread}|${closeSpread} ${coin} `;
      }
  }, [statistics]);

  return (
      <UpperInfoStyled>
          <TitlesContainer>
            <InfoSettings>
              <p style={{ color: themeStore.textColor }}>
                Вход %:
                <input
                  value={input}
                  ref={inputRef}
                  type='number'
                  onChange={e => setInput(e.target.value)}
                  onClick={() => {
                    //@ts-ignore
                    inputRef.current.select()
                  }}
                  style={{
                    color: themeStore.textColor,
                    backgroundColor: themeStore.bgColor,
                    border: `1px solid ${themeStore.textColor}`,
                  }}
                />
              </p>
              <p style={{ color: themeStore.textColor }}>
                Выход %:
                <input
                  value={output}
                  ref={outputRef}
                  type="number"
                  onChange={e => setOutput(e.target.value)}
                  onClick={() => {
                    //@ts-ignore
                    outputRef.current.select()
                  }}
                  style={{
                    color: themeStore.textColor,
                    backgroundColor: themeStore.bgColor,
                    border: `1px solid ${themeStore.textColor}`,
                  }}
                />
              </p>
            </InfoSettings>
            <Info style={{ borderColor: themeStore.textColor, backgroundColor: BLUE, marginLeft: "12%", marginRight: "12%"}}>
              <p style={{color: WHITE}}>Спред: {enterSpread}%</p>
            </Info>
          </TitlesContainer>

          <LongShortContainer>
              {statistics?.longShort === "short"
                ?
                  <>
                    <DexContainer statistics={statistics}/>
                    <LongShortContainerComp statistics={statistics}/>
                  </>
                :
                  <>
                    <LongShortContainerComp statistics={statistics}/>
                    <DexContainer statistics={statistics}/>
                  </>
              }
          </LongShortContainer>
      </UpperInfoStyled>
  )
});

const DexContainer = (props: Partial<IUpperInfo>) => {
  const {statistics} = props

  const links = [
    {
      link: statistics?.swapLink,
      name: "1inch"
    },
    {
      link: statistics?.dexscreenerUrl,
      name: "Dexscr"
    },
    {
      link: statistics?.gmgnUrl,
      name:"GMGN"
    },
    {
      link: statistics?.defillamaUrl,
      name: "Lama"
    },
    {
      link: statistics?.goplusUrl,
      name: "GoPlus"
    },
    {
      link: statistics?.cmcUrl?.[0] ? `https://coinmarketcap.com/currencies/${statistics.cmcUrl?.[0]}` : undefined,
      name: `№ ${statistics?.cmcUrl?.[1]}`
    },
  ].filter(link => link.link)

  return (
    <>
      {statistics &&
        <div style={{ borderColor: themeStore.textColor, color: themeStore.textColor}}>
            <p id="linksArray" style={{lineHeight: "1em", minHeight: '32px', width: 'fit-content', textWrap: 'pretty', textAlign: 'center', display: 'initial'}} className="row">
              {
                links.map((link, i) => {
                  return (
                    <>
                      <LongShortTitle {...link}/>
                      {i < links.length - 1 ? " | " : ''}
                      {i === 2 ? <br /> : null}
                    </>
                  )
                })
              }

            </p>
            <p>Сеть: {statistics.chain}</p>
            <p>Ликвидность: {statistics.liquidity}</p>
            <p>24Ч объем: {statistics.volumeDex}</p>
            <span style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '.5em'}}><p>{"СА: "}</p> <StringContainer style={{width: 'fit-content'}} content={statistics.contract} allowCopy short/></span>
            <span style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '.5em'}}><p>{"Пул: "}</p> <StringContainer style={{width: 'fit-content'}} content={statistics.pool} allowCopy short/></span>
            <p>Такс: {statistics.poolFee ? statistics.poolFee + '%' : ' - '}, buy({statistics.extraBuyFee ? statistics.extraBuyFee + '%' : ' - '}), sell({statistics.extraSellFee ? statistics.extraSellFee + '%' : ' - '})</p>
        </div>
      }
    </>
  )
}

const LongShortContainerComp = (props: Partial<IUpperInfo>) => {
  const {statistics} = props
  const tick = " ✅"
  const x = " ❌"

  return (
    <>
      {statistics &&
        <div style={{ borderColor: themeStore.textColor }}>
          <p style={{textAlign: 'center', minHeight: '32px', display: 'initial'}}>
            <LongShortTitle link={statistics?.futuresUrl} name={statistics?.name}/>
          </p>
          {Object.entries(getLongShortTitles(statistics)).map(entries =>
              <p key={`longshort-${entries[0]}`} style={{ color: themeStore.textColor, textWrap: 'nowrap' }}>{entries[0]}: {entries[1]}</p>
          )}
          <p style={{ color: themeStore.textColor, textWrap: 'nowrap' }}>Ввод: {statistics?.deposit ? tick : x} Вывод: {statistics?.withdraw ? tick : x}</p>
        </div>
      }
    </>


  )
}

const LongShortTitle = memo((options: {
  link?: string
  name?: string
}) => {
  const {link, name} = options

  return (
    <>
      {link ? <Link to={link || "/"} target='_blank'>{name}</Link> : null}
    </>

  )
}, (prev, next) => {
  return prev.link === next.link && prev.name === next.name
})

export default UpperInfo
